import { createContext, useContext, type ReactNode } from "react";

import type {
  CompetitionDetail,
  CompetitionTeamBrowseRow,
  Deliverable,
  LeaderboardRow,
  MyTeamPayload,
  PendingJoinRequest,
  Student,
  TeamDeliverableStatusRow,
  TeamSubmissionRow,
} from "../../types/database";
import type { MyAnnouncement } from "../../lib/announcements";

/**
 * Shared state contract between the competition shell (_layout.tsx) and the
 * child tabs (index/deliverables/team/info). _layout owns the data fetch +
 * realtime subscriptions; children render under the shared shell + sidebar
 * and read from this context so the chrome + case-progress badge never
 * remounts when the user switches tabs.
 */
export type CompetitionShellValue = {
  /** Route param echoed for child convenience. */
  competitionId: string;

  /** Full competition row. `null` = not found (404 in UI); `undefined` = loading. */
  competition: CompetitionDetail | null | undefined;

  /** Is the student registered for this competition? */
  registered: boolean;

  /** Whether the registration check has completed at least once. */
  hasLoaded: boolean;

  /** Whether the student is currently eligible to register. */
  eligible: boolean;

  /** Current registration status string ("registered" / "submitted" / null). */
  regStatus: string | null;

  /** The student's team for this competition, if any. */
  myTeam: MyTeamPayload | null;

  /** Current student profile. */
  student: Student | null;

  /** Deliverables (team-aware: server redacts locked-gate rows). */
  deliverables: Deliverable[];

  /** Submissions made by this team. */
  teamSubmissions: TeamSubmissionRow[];

  /** Per-deliverable status rows (judging outcomes). */
  teamStatuses: TeamDeliverableStatusRow[];

  /** Leaderboard for the competition (dashboard tab uses this). */
  leaderboardRows: LeaderboardRow[];

  /** Announcements scoped to this competition. */
  announcements: MyAnnouncement[];

  /** Update the announcements list (overlay marks-as-viewed locally). */
  setAnnouncements: (next: MyAnnouncement[] | ((prev: MyAnnouncement[]) => MyAnnouncement[])) => void;

  /** Public-team browse list (team-tab Find Team flow). */
  browseTeams: CompetitionTeamBrowseRow[];

  /** Join requests pending against the student's own team (captain view). */
  pending: PendingJoinRequest[];

  /** Shared "in-flight mutation" flag — tabs disable buttons while true. */
  busy: boolean;
  setBusy: (b: boolean) => void;

  /** Shared "last mutation error" message displayed near tab actions. */
  actionErr: string | null;
  setActionErr: (err: string | null) => void;

  /** Manual refresh trigger — call after any mutation that needs new data. */
  refresh: () => Promise<void>;
};

const CompetitionShellContext = createContext<CompetitionShellValue | null>(null);

export function CompetitionShellProvider({
  value,
  children,
}: {
  value: CompetitionShellValue;
  children: ReactNode;
}) {
  return (
    <CompetitionShellContext.Provider value={value}>
      {children}
    </CompetitionShellContext.Provider>
  );
}

export function useCompetitionShell(): CompetitionShellValue {
  const ctx = useContext(CompetitionShellContext);
  if (!ctx) {
    throw new Error(
      "useCompetitionShell must be used within a CompetitionShellProvider " +
        "(in mobile/app/(student)/student/competition/[id]/_layout.tsx)",
    );
  }
  return ctx;
}
